import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  LayerStoreService,
  ORI_LAYER,
  STATE_LEVEL_LAYER,
} from '../../../../mapbox/services/layer-store.service';
import { LayersDataService } from '../layers-data.service';
import { ColorscaleService } from '../../../../mapbox/visualization/colorscale/colorscale.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { combineLatest, debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ChartDataByYear,
  FeatureStatus,
  PopulationService,
} from '../population-menu/population.service';
import { POIsFeatureStatus, PoisService } from '../pois/pois.service';
import { TargetAudienceService } from '../target-audience/target-audience.service';
import { HealthWealthWiseService } from '../health-wealth-wise/health-wealth-wise.service';
import { IncomeDistributionService } from '../wealth-menu/income-distribution.service';
import { HomeownershipService } from '../homeownership/homeownership.service';
import { EducationService } from '../education/education.service';
import { HealthService } from '../health/health.service';
import { CommuteAndMobilityService } from '../commute-and-mobility/commute-and-mobility.service';
import { SelectedCellService } from '../../../../mapbox/services/selected-cell.service';
import {
  COMMUTE_AND_MOBILITY_GROUP_TITLE,
  CRIME_GROUP_TITLE,
  EDUCATION_GROUP_TITLE,
  HEALTH_GROUP_TITLE,
  HOUSING_GROUP_TITLE,
  HWW_GROUP_TITLE,
  LayersMenuService,
  PHYSICAL_ENVIRONMENT_GROUP_TITLE,
  POI_GROUP_TITLE,
  POPULATION_GROUP_TITLE,
  ROAD_DENSITY_GROUP_TITLE,
  TARGET_AUDIENCE_GROUP_TITLE,
  WEALTH_GROUP_TITLE,
} from '../layers-menu.service';
import {
  ALL_DATA_GROUP,
  CHARTS_GROUP,
  MAPS_GROUP,
  MenuGroupType,
  PLACES_GROUP,
} from '../../../../map.component';
import { CrimeService } from '../crime/crime.service';
import { MapBoxService } from '../../../../mapbox/mapbox.service';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../../../../user/authentication.service';
import { ModalService } from '../../../../../shared/services/modal.service';
import {
  hwwFeatures,
  SCHOOLS_POI,
} from '../../../../../shared/types/feature-data-type';
import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { IsGroupAccessiblePipe } from '../shared/is-group-accessible.pipe';
import { GroupTitleFormatterPipe } from './group-title-formatter.pipe';
import { FeatureTooltipComponent } from '../shared/feature-tooltip/feature-tooltip.component';
import { IsFeatureAccessiblePipe } from '../shared/is-feature-accessible.pipe';
import { LayerNameFormatPipe } from '../../../../common-colorscale/layer-name-format.pipe';
import { ColorScaleComponent } from '../../../../mapbox/visualization/colorscale/color-scale.component';
import { ChartBlockComponent } from '../shared/chart-block/chart-block.component';
import { FeatureDisclaimerComponent } from '../shared/feature-disclaimer/feature-disclaimer.component';
import { Data, Layout } from 'plotly.js-dist-min';
import { CustomRadioComponent } from '../../../../../shared/components/custom-radio/custom-radio.component';
import { CustomRadioGroupComponent } from '../../../../../shared/components/custom-radio-group/custom-radio-group.component';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconButtonDirective } from '../../../../../shared/directives/icon-button.directive';
import {
  CustomSlideToggleComponent,
  SlideToggleChange,
} from '../../../../../shared/components/custom-slide-toggle/custom-slide-toggle.component';
import { SubscriptionPlans } from '../../../../../user/user/user.model';
import { chartToMap } from '../pois/PoiDistribution';
import { MenuSearchService } from '../menu-search/menu-search.service';
import { generateInaccessibleModalData } from '../shared/feature-tooltip/inacessible-features-util';
import { NearestPoiPanelComponent } from '../shared/poi-distance-block/poi-distance-block.component';
import { UserGuideService } from '../../../../user-guide/user-guide.service';

export interface MenuGroupData {
  title: string;
  featureStatus: FeatureStatus | null;
  e2eName?: string;
  disclaimer?: MenuGroupDisclaimer;
  scope?: {
    default?: FeatureScope;
    scopeDependentLayers?: string[];
  };

  tooltip?: {
    text: string;
    routerLink: string;
  };

  data: {
    mapColor: MapColorControlBlock[] | null;
    charts: ChartControlBlock[] | null;
    places: (PointsControlBlock | ChartControlBlock)[] | null;
  };
}

export type MenuGroupDisclaimer = {
  text: string;
  visibleOnLevels?: string[];
  visibleOnZoom?: {
    min?: number;
    max?: number;
  };
};

export type FeatureTooltip = {
  headerText: string;
  text: string;
} | null;

export interface MapColorControlBlock {
  featureName: string;
  featureConst: string;
  isAdminOnly?: boolean;
  filterGlobalMin: number | undefined;
  filterGlobalMax: number | undefined;
  e2eName?: string;
  disclaimer?: {
    text: string;
    visibleOnLevels?: string[];
  };
  accessibleOnLevels?: string[];
  tooltip: {
    headerText: string;
    text: string;
  } | null;
}

export interface ChartControlBlock {
  toggleTitle: string;
  chartName: string;
  // used for automated testing element navigation
  e2eName?: string;
  isAdminOnly?: boolean;
  plotlyChart: {
    data: Data;
    layout: Layout;
  } | null;
  yearVariable: string | null;
  featureConst: string;
  containsChartDataVariable: string;
  accessibleYears: number[] | null;
  chartStyle?: { [p: string]: any };
  comingSoon?: boolean;
  customText?: string;
  disclaimer?: {
    text: string;
    visibleOnLevels?: string[];
  };
  chartsByYears?: ChartDataByYear;
  tooltip: {
    headerText: string;
    text: string;
  } | null;
}

export interface PointsControlBlock {
  featureName: string;
  featureConst: string;
  e2eName?: string;
  isAdminOnly?: boolean;
  disclaimer?: {
    text: string;
    visibleOnLevels?: string[];
    visibleOnZoom?: {
      min?: number;
      max?: number;
    };
  };
  accessibleOnLevels?: string[];
  tooltip: {
    headerText: string;
    text: string;
  } | null;
}

export type LinkedToggles = Record<
  string,
  { charts: string[] | never[]; places: string[] | never[] }
>;

export type SubscriptionToLinkedToggles = {
  [plan in SubscriptionPlans]: LinkedToggles;
} & {
  trial: LinkedToggles;
  unauthorized: LinkedToggles;
};

export enum FeatureScope {
  BY_COUNTRY = '_by_country',
  BY_STATE = '_by_state',
}

@Component({
  selector: 'app-menu-group',
  templateUrl: './menu-group.component.html',
  styleUrls: [
    './menu-group.component.scss',
    '../../../../../app.component.scss',
  ],
  imports: [
    NgIf,
    AsyncPipe,
    NgClass,
    IconComponent,
    IsGroupAccessiblePipe,
    GroupTitleFormatterPipe,
    FeatureTooltipComponent,
    FormsModule,
    NgForOf,
    IsFeatureAccessiblePipe,
    LayerNameFormatPipe,
    ColorScaleComponent,
    ChartBlockComponent,
    FeatureDisclaimerComponent,
    ReactiveFormsModule,
    CustomRadioComponent,
    CustomRadioGroupComponent,
    IconButtonDirective,
    CustomSlideToggleComponent,
    NearestPoiPanelComponent,
  ],
})
export class MenuGroupComponent implements OnInit, OnDestroy {
  @Input() groupData!: MenuGroupData;
  @Input() form!: FormGroup;
  @Input() isPaidPlan!: boolean | null;

  @Input() activeMenuGroup!: MenuGroupType;
  @Input() isFiltersOpened!: boolean;
  @Input() isFeatureVisible = false;

  private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();
  // Looks stupid, but i didn't find better solution to call fillChartsData separately,
  // general calls leads to too many requests
  private groupNameToServiceNameMap = new Map<string, string>([
    [POPULATION_GROUP_TITLE, 'ageSexService'],
    [HWW_GROUP_TITLE, 'hwwService'],
    [WEALTH_GROUP_TITLE, 'incomeDistributionService'],
    [TARGET_AUDIENCE_GROUP_TITLE, 'targetAudienceService'],
    [HOUSING_GROUP_TITLE, 'homeownershipService'],
    [EDUCATION_GROUP_TITLE, 'educationService'],
    [HEALTH_GROUP_TITLE, 'healthService'],
    [ROAD_DENSITY_GROUP_TITLE, 'roadDensityService'],
    [POI_GROUP_TITLE, 'POIsService'],
    [CRIME_GROUP_TITLE, 'crimeService'],
    [PHYSICAL_ENVIRONMENT_GROUP_TITLE, 'physicalEnvironmentService'],
    [COMMUTE_AND_MOBILITY_GROUP_TITLE, 'commuteAndMobilityService'],
  ]);

  public $updatePoiToggleMap = new Subject();

  public wasYearSelected: boolean = false;

  public chartFeatureList: string[] = [];
  public placesFeatureList: string[] = [];
  public mapColorFeatureList: string[] = [];

  public readonly FeatureScope = FeatureScope;
  public selectedScope!: FeatureScope;

  private relatedServiceName!: string;

  public guideService = inject(UserGuideService);

  constructor(
    public layerStore: LayerStoreService,
    private layersMenuService: LayersMenuService,
    public layerDataService: LayersDataService,
    public colorScaleService: ColorscaleService,
    public accessService: UserAccessService,
    private mapboxService: MapBoxService,
    private selectedCellService: SelectedCellService,
    private ageSexService: PopulationService,
    private targetAudienceService: TargetAudienceService,
    private hwwService: HealthWealthWiseService,
    private incomeDistributionService: IncomeDistributionService,
    private homeownershipService: HomeownershipService,
    private educationService: EducationService,
    private commuteAndMobilityService: CommuteAndMobilityService,
    private healthService: HealthService,
    private POIsService: PoisService,
    private crimeService: CrimeService,
    private modalService: ModalService,
    private router: Router,
    public authService: AuthenticationService,
    private searchService: MenuSearchService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.handleSubscriptions();
    this.handleInputData();

    this.isFeatureVisible = this.layerDataService.getIsGroupOpened(
      this.groupData.title
    );
  }

  ngOnDestroy() {
    this.destroy$.emit(true);
    this.destroy$.complete();
  }

  public onActiveLayerToggle(layer: string): void {
    this.form.get('activeLayer')!.setValue(layer);

    if (this.layerStore.activeLayer.value !== layer) {
      this.layerDataService.handleOpenedGroup(this.groupData.title, true);
      this.layerStore.handleLayerChange(layer);
    }
  }

  public handleRadioButtonClick(): void {
    if (!this.accessService.checkAccessIfUnauthorized()) {
      return;
    }
  }

  public handleInaccessibleFeatureClick(
    feature: string,
    featureName: string
  ): void {
    if (!this.accessService.userHasAccess(feature)) {
      this.modalService.openModal(
        generateInaccessibleModalData(
          featureName,
          this.accessService.isUnauthorized,
          feature,
          this.accessService.getIsTrial()
        )
      );
      // this.router.navigate([this.authService.hasAccessToken() ? 'user/subscription'  : 'sign-in'])
    }
  }

  public handleSubscriptions(): void {
    this.accessService.isUserPlanUpdated
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated) => {
        if (updated) {
          this.layersMenuService.reassignTooltipText(this.groupData);

          // Force change detection to rerender menu as it could stuck after login/logout
          this.cdr.detectChanges();
        }
      });

    this.layerStore.activeLayer
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((activeLayer) => {
        // Trigger linked features on layer change and on init
        this.handleLinkedToggles(activeLayer);

        this.isFeatureVisible = this.layerDataService.getIsGroupOpened(
          this.groupData.title
        );

        if (this.groupData.scope) {
          this.selectedScope =
            (this.layerStore.activeLayer.value.endsWith(FeatureScope.BY_STATE)
              ? FeatureScope.BY_STATE
              : FeatureScope.BY_COUNTRY) ?? this.groupData.scope.default!;
        }

        const isFeatureActive =
          this.mapColorFeatureList.includes(activeLayer) ||
          (hwwFeatures.includes(activeLayer) &&
            this.groupData.title === HWW_GROUP_TITLE);

        if (isFeatureActive) {
          this.layerDataService.handleOpenedGroup(this.groupData.title, true);
          this.isFeatureVisible = true;
        }
      });

    if (this.groupData.scope) {
      combineLatest([
        this.layerStore.activeLayer,
        this.layerStore.activeLevel,
      ]).subscribe(([layer, level]) => {
        if (
          level === STATE_LEVEL_LAYER &&
          this.selectedScope !== FeatureScope.BY_COUNTRY
        ) {
          this.onScopeToggle(FeatureScope.BY_COUNTRY);
        }
      });
    }

    // Update isFeatureVisible depending on menu search query
    this.searchService.searchQuery$
      .pipe(takeUntil(this.destroy$), debounceTime(50))
      .subscribe(([_, query]) => {
        if (query) {
          this.isFeatureVisible = !!query.trim();
          this.layerDataService.handleOpenedGroup(
            this.groupData.title,
            this.isFeatureVisible
          );
        }
      });
  }

  private handleLinkedToggles(layer: string): void {
    const currentPlan = this.accessService.getUserPlan();
    const isTrial = this.accessService.getIsTrial();
    const isUnauthorized = this.accessService.getIsUnauthorized();

    const linkedToggles = currentPlan
      ? this.layerDataService.linkedToggles[currentPlan]
      : isTrial
        ? this.layerDataService.linkedToggles.trial
        : this.layerDataService.linkedToggles.unauthorized;

    if (hwwFeatures.includes(layer)) {
      layer = layer.split('_by_')[0];
    }

    if (!linkedToggles || !linkedToggles[layer]) {
      return;
    }

    if (linkedToggles[layer].places && linkedToggles[layer].places.length > 0) {
      linkedToggles[layer].places.forEach((place) => {
        const control = this.layerDataService.placesToggleMap.get(place);
        if (control) {
          control.setValue(true);

          const groupTitle =
            this.layerDataService.getGroupNameByFeatureConst(place);

          this.layerDataService.handleOpenedGroup(groupTitle, true);

          switch (place) {
            case SCHOOLS_POI:
              this.educationService.setPointsLayerVisibility(true);
              break;
            case ORI_LAYER:
              this.crimeService.setPointsLayerVisibility(true);
              break;
          }
        }
      });
    }

    if (linkedToggles[layer].charts && linkedToggles[layer].charts.length > 0) {
      linkedToggles[layer].charts.forEach((chart) => {
        const groupTitle =
          this.layerDataService.getGroupNameByFeatureConst(chart);

        this.layerDataService.handleOpenedGroup(groupTitle, true);

        if (this.layerDataService.toggleMap.has(chart)) {
          this.layerDataService.toggleMap.get(chart)?.setValue(true);
        }
      });
    }
  }

  private updateActivePOITogglesArray(toggles: { [key: string]: boolean }) {
    this.$updatePoiToggleMap.next('');

    const poiStatus = this.groupData.featureStatus as POIsFeatureStatus;
    if (!poiStatus || !poiStatus.activePoisCharts) {
      return;
    }

    Object.entries(toggles).forEach(([key, value]) => {
      // Only process valid POI fetched from /pois
      if (!chartToMap.has(key)) {
        return;
      }

      const activeCharts = (this.groupData.featureStatus! as POIsFeatureStatus)
        .activePoisCharts;
      if (value && !activeCharts!.includes(key)) {
        activeCharts!.push(key);
      } else if (!value) {
        const index = activeCharts!.indexOf(key);
        if (index != -1) {
          activeCharts!.splice(index, 1);
        }
      }
    });

    this.POIsService.handlePoisOnMap();
    this.POIsService.handlePoiLayerVisibility();
  }

  public onSelectedYearChange(data: { year: number; usedFor: string }): void {
    if (!this.accessService.checkAccessIfUnauthorized()) {
      return;
    }

    this.wasYearSelected = true;
    // @ts-ignore
    this.groupData.featureStatus[data.usedFor] = data.year;

    // Age/sex has it's own structure and comes will all available years at once
    if (data.usedFor !== 'ageSexYear') {
      this.handleFillChartsData();
    }
  }

  public toggleGroup(): void {
    this.isFeatureVisible = !this.isFeatureVisible;
    this.layerDataService.handleOpenedGroup(
      this.groupData.title,
      this.isFeatureVisible
    );

    if (this.isFeatureVisible) {
      this.handleFillChartsData();
    }
  }

  private openDefaultGroup(): void {
    this.layerDataService.handleOpenedGroup(
      this.groupData.title,
      this.mapColorFeatureList.includes(this.layerStore.activeLayer.value)
    );
    this.isFeatureVisible = this.layerDataService.getIsGroupOpened(
      this.groupData.title
    );
  }

  private handleInputData(): void {
    this.relatedServiceName = this.groupNameToServiceNameMap.get(
      this.groupData.title
    )!;

    if (this.groupData.data.charts) {
      this.groupData.data.charts.forEach((chart) => {
        this.layerDataService.toggleMap.set(
          chart.featureConst,
          new FormControl(false)
        );
      });

      this.chartFeatureList = Object.values(this.groupData.data.charts).map(
        ({ featureConst }) => featureConst
      );
    }

    if (this.groupData.data.places) {
      this.groupData.data.places.forEach((place) => {
        this.layerDataService.placesToggleMap.set(
          place.featureConst,
          new FormControl(false)
        );
      });

      this.layerDataService.placesToggleMap.forEach((control, key) => {
        if (control) {
          control.valueChanges.subscribe((value) => {
            this.updateActivePOITogglesArray({ [key]: value });
          });
        }
      });

      this.placesFeatureList = Object.values(this.groupData.data.places).map(
        ({ featureConst }) => featureConst
      );
    }

    if (this.groupData.data.mapColor) {
      this.mapColorFeatureList = Object.values(
        this.groupData.data.mapColor
      ).map(({ featureConst }) => featureConst);
    }

    this.openDefaultGroup();
  }

  private handleFillChartsData(): void {
    if (this.selectedCellService.isCellSelected()) {
      if ((this as any)[this.relatedServiceName]?.fillChartsData) {
        (this as any)[this.relatedServiceName].fillChartsData(
          this.selectedCellService.getSelectedFeature()
        );
      }
    }
  }

  public onScopeToggle(scope: FeatureScope): void {
    if (!this.accessService.checkAccessIfUnauthorized()) {
      return;
    }

    const acceptableLayers = this.groupData.scope!.scopeDependentLayers || [];

    this.selectedScope = scope;

    const currentLayer = this.layerStore.activeLayer.value;

    if (acceptableLayers.some((layer) => currentLayer!.startsWith(layer))) {
      const baseLayer = acceptableLayers.find((layer) =>
        currentLayer!.startsWith(layer)
      );
      const newActiveLayer = baseLayer + this.selectedScope;

      this.form.get('activeLayer')!.setValue(newActiveLayer);

      this.layerStore.handleLayerChange(newActiveLayer);
    }
  }

  public handlePointsToggleChange(event: SlideToggleChange): void {
    if (!this.accessService.checkAccessIfUnauthorized()) {
      return;
    }

    if ((this as any)[this.relatedServiceName].setPointsLayerVisibility) {
      (this as any)[this.relatedServiceName].setPointsLayerVisibility(
        event.checked
      );
    }
  }

  public identifyByConst(index: number, item: any): string {
    return item.featureConst;
  }

  public handleTooltipModalOpen(modalData: any): void {
    this.modalService.openModal(modalData);
  }

  protected readonly STATE_LEVEL_LAYER = STATE_LEVEL_LAYER;
  protected readonly MAPS_GROUP = MAPS_GROUP;
  protected readonly CHARTS_GROUP = CHARTS_GROUP;
  protected readonly PLACES_GROUP = PLACES_GROUP;
  protected readonly ALL_DATA_GROUP = ALL_DATA_GROUP;
  protected readonly POI_GROUP_TITLE = POI_GROUP_TITLE;
  protected readonly EDUCATION_GROUP_TITLE = EDUCATION_GROUP_TITLE;
  protected readonly CRIME_GROUP_TITLE = CRIME_GROUP_TITLE;
  protected readonly SCHOOLS_POI = SCHOOLS_POI;
  protected readonly ORI_LAYER = ORI_LAYER;
}
